import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import {
  AutocompleteMultiSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  toast,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { readShigoAppConfig } from '../../../../../lib/config';
import { isAssignableShigoRole, ShigoRole } from '../../../../auth/permissions';
import { formatShigoRole } from '../../../../auth/roleLabels';
import {
  useAdminUserAllowedRolesQuery,
  useAdminUserAllowedRolesQueries,
  useAdminUserMutations,
  useAdminUserNetworksQuery,
  useAdminUserPublisherOptions,
} from '../../hooks';
import { AdminUsersContext, type AdminUser, type AdminUserMutationPayload } from '../../model';
import { adminUserFormSchema, type AdminUserFormValue } from './UserDialog.schema';
import {
  clearAgencySelection,
  applyApiFieldErrors,
  emptyAllowedRolesQuery,
  membershipsForUser,
  networksForUser,
  translateValidationMessage,
} from './UserDialog.helpers';
import { UserNetworkMembershipFields } from './UserNetworkMembershipFields';
import { UserAgencyFields } from './UserAgencyFields';
import { UserPasswordField } from './UserPasswordField';

type UserDialogProps = {
  context: AdminUsersContext;
  user: AdminUser | null;
  onClose: () => void;
};

const superAdminRole = ShigoRole.SuperAdmin;
const superAdminRoleValue: string = superAdminRole;

export function UserDialog({ context, user, onClose }: UserDialogProps) {
  'use no memo';

  const creating = user === null;
  const superAdmin = context === AdminUsersContext.SuperAdmin;
  const deployment = readShigoAppConfig();
  const mutations = useAdminUserMutations(context);
  const networks = useAdminUserNetworksQuery(superAdmin);
  const initialMemberships = membershipsForUser(user);
  const form = useForm<AdminUserFormValue>({
    resolver: zodResolver(adminUserFormSchema),
    defaultValues: {
      email: user?.email ?? '',
      name: user?.name ?? '',
      password: '',
      agencyIds: [...(user?.agencyIds ?? [])],
      roles: [...(user?.roles ?? [])],
      publisherIds: [
        ...(user?.publisherIds ?? user?.publishers.map((publisher) => publisher.id) ?? []),
      ],
      networkId: superAdmin ? '' : deployment.networkId,
      memberships: initialMemberships,
      creating,
      superAdmin,
    },
  });
  const { errors } = useFormState({ control: form.control });
  const formValues = useWatch({ control: form.control });
  const roles = useWatch({ control: form.control, name: 'roles' });
  const networkId = useWatch({ control: form.control, name: 'networkId' });
  const memberships = useWatch({ control: form.control, name: 'memberships' });
  const [unavailablePublisherMemberships, setUnavailablePublisherMemberships] = React.useState(
    () => new Set<string>(),
  );
  const allowedRoles = useAdminUserAllowedRolesQuery(networkId);
  const membershipNetworkIds = memberships.map((membership) => membership.networkId);
  const membershipAllowedRoles = useAdminUserAllowedRolesQueries(
    superAdmin ? membershipNetworkIds : [],
  );
  const publisherRoleSelected = roles.includes(ShigoRole.Publisher);
  const publishers = useAdminUserPublisherOptions({
    context,
    networkId,
    enabled: publisherRoleSelected,
  });
  const pending = mutations.create.isPending || mutations.update.isPending;
  const guard = useBusyDialogGuard(pending, onClose);
  const roleOptionsUnavailable = superAdmin
    ? membershipAllowedRoles.some((query) => query.isFetching || query.isError)
    : allowedRoles.isFetching || allowedRoles.isError;
  const publisherOptionsUnavailable =
    (!superAdmin && publisherRoleSelected && (publishers.loading || publishers.error)) ||
    (superAdmin && unavailablePublisherMemberships.size > 0);
  const saveDisabled =
    pending ||
    !adminUserFormSchema.safeParse(formValues).success ||
    roleOptionsUnavailable ||
    publisherOptionsUnavailable ||
    (superAdmin && (networks.isFetching || networks.isError));
  const immutableSuperAdmin = !superAdmin && (user?.roles ?? []).includes(superAdminRole);
  const roleOptions = (allowedRoles.data ?? []).filter(
    (role) => superAdmin || role !== ShigoRole.SuperAdmin,
  );
  const visibleRoleOptions = immutableSuperAdmin
    ? [...new Set([...roleOptions, ShigoRole.SuperAdmin])]
    : roleOptions;
  const networkOptions = networksForUser(networks.data ?? [], user);
  const setPublisherMembershipUnavailable = React.useCallback(
    (membershipNetworkId: string, unavailable: boolean) => {
      setUnavailablePublisherMemberships((current) => {
        if (current.has(membershipNetworkId) === unavailable) return current;
        const next = new Set(current);
        if (unavailable) next.add(membershipNetworkId);
        else next.delete(membershipNetworkId);
        return next;
      });
    },
    [],
  );

  async function submit(values: AdminUserFormValue) {
    if (superAdmin && user && !user.id?.trim()) {
      form.setError('root.server', { message: dashboardT('users.saveError') });
      return;
    }
    const mutableRoles = values.roles.filter(
      (role) => !(immutableSuperAdmin && role === superAdminRoleValue),
    );
    const roleOptionSet = new Set(roleOptions);
    if (
      !superAdmin &&
      (allowedRoles.isError ||
        allowedRoles.isFetching ||
        mutableRoles.some((role) => !isAssignableShigoRole(role) || !roleOptionSet.has(role)))
    ) {
      form.setError('roles', { message: 'users.validation.rolesNotAllowed' });
      return;
    }
    const selectedRoles = [
      ...new Set([...mutableRoles, ...(immutableSuperAdmin ? [superAdminRole] : [])]),
    ];
    if (superAdmin) {
      const invalidMembershipIndex = values.memberships.findIndex((membership, index) => {
        const allowed = membershipAllowedRoles[index];
        const allowedRoleSet = new Set(allowed?.data ?? []);
        return (
          !allowed ||
          allowed.isError ||
          allowed.isFetching ||
          membership.roles.some((role) => !isAssignableShigoRole(role) || !allowedRoleSet.has(role))
        );
      });
      if (invalidMembershipIndex >= 0) {
        const rolesPath =
          `memberships.${String(invalidMembershipIndex)}.roles` as `memberships.${number}.roles`;
        form.setError(rolesPath, {
          message: 'users.validation.rolesNotAllowed',
        });
        return;
      }
    }
    const membershipPayloads = values.memberships.map((membership) => {
      const savedMembership = user?.memberships.find(
        (candidate) => String(candidate.networkId) === membership.networkId,
      );
      return {
        networkId: Number(membership.networkId),
        enabled: savedMembership?.enabled ?? true,
        roles: [...membership.roles],
        roleIds: [...(savedMembership?.roleIds ?? [])],
        rights: [...(savedMembership?.rights ?? [])],
        publisherIds: membership.roles.includes(ShigoRole.Publisher)
          ? [...membership.publisherIds]
          : [],
        agencyIds: membership.roles.includes(ShigoRole.Agency) ? [...membership.agencyIds] : [],
        contentOwnerIds: [...(savedMembership?.contentOwnerIds ?? [])],
        advertiserId: savedMembership?.advertiserId ?? null,
      };
    });
    // The backend only applies `password` when it is present and non-blank, so an empty
    // value on edit is a no-op that keeps the current password. Regular (non-Super Admin)
    // updates do not accept a password change, so only send it when creating or editing a
    // Super Admin user with a new value entered.
    const includePassword = creating || (superAdmin && values.password.length > 0);
    const payload: AdminUserMutationPayload = {
      ...(user?.username ? { username: user.username } : {}),
      email: values.email.trim(),
      name: values.name.trim(),
      ...(superAdmin
        ? {
            enabled: user?.enabled ?? true,
            memberships: membershipPayloads,
          }
        : {
            roles: selectedRoles,
            agencyIds: selectedRoles.includes(ShigoRole.Agency) ? [...values.agencyIds] : [],
            publisherIds: selectedRoles.includes(ShigoRole.Publisher)
              ? [...values.publisherIds]
              : [],
          }),
      ...(includePassword ? { password: values.password } : {}),
    };

    try {
      if (user) await mutations.update.mutateAsync({ user, payload });
      else {
        await mutations.create.mutateAsync({ payload });
      }
      toast.success(dashboardT(user ? 'users.updated' : 'users.created'));
      onClose();
    } catch (error) {
      applyApiFieldErrors(error, form.setError);
      form.setError('root.server', { message: dashboardT('users.saveError') });
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className={`${superAdmin ? 'max-w-[760px]' : 'max-w-[600px]'} gap-0 overflow-visible p-0`}
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT(user ? 'users.form.editTitle' : 'users.form.createTitle')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('users.form.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
              <div className={superAdmin ? 'grid gap-4 sm:grid-cols-2' : 'space-y-4'}>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold" htmlFor="admin-user-email">
                    {dashboardT('users.email')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="admin-user-email"
                    type="email"
                    required
                    disabled={!creating}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'admin-user-email-error' : undefined}
                    {...form.register('email')}
                  />
                  {errors.email?.message ? (
                    <p id="admin-user-email-error" className="text-destructive text-xs">
                      {translateValidationMessage(errors.email.message)}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold" htmlFor="admin-user-name">
                    {dashboardT('users.name')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="admin-user-name"
                    required
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'admin-user-name-error' : undefined}
                    {...form.register('name')}
                  />
                  {errors.name?.message ? (
                    <p id="admin-user-name-error" className="text-destructive text-xs">
                      {translateValidationMessage(errors.name.message)}
                    </p>
                  ) : null}
                </div>
              </div>

              {creating || superAdmin ? (
                <UserPasswordField
                  form={form}
                  errors={errors}
                  pending={pending}
                  optional={!creating}
                />
              ) : null}

              {!superAdmin && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold">
                      {dashboardT('users.roles')} <span className="text-destructive">*</span>
                    </Label>
                    <Controller
                      control={form.control}
                      name="roles"
                      render={({ field }) => (
                        <AutocompleteMultiSelect
                          label={dashboardT('users.roles')}
                          options={visibleRoleOptions.map((role) => ({
                            id: role,
                            label: formatShigoRole(role),
                            disabled: immutableSuperAdmin && role === superAdminRole,
                          }))}
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            clearAgencySelection(form, value);
                            if (
                              value.length > 0 &&
                              form.getFieldState('roles').error?.message ===
                                'users.validation.rolesRequired'
                            ) {
                              form.clearErrors('roles');
                            }
                          }}
                          loading={allowedRoles.isFetching}
                          disabled={
                            !networkId || allowedRoles.isFetching || allowedRoles.isError || pending
                          }
                          placeholder={dashboardT(
                            allowedRoles.isFetching ? 'users.rolesLoading' : 'users.selectRoles',
                          )}
                          emptyMessage={dashboardT('users.noAllowedRoles')}
                        />
                      )}
                    />
                    {errors.roles?.message ? (
                      <p className="text-destructive text-xs">
                        {translateValidationMessage(errors.roles.message)}
                      </p>
                    ) : null}
                    {allowedRoles.isError ? (
                      <p className="text-destructive text-xs">{dashboardT('users.rolesError')}</p>
                    ) : null}
                    {!allowedRoles.isFetching &&
                    !allowedRoles.isError &&
                    visibleRoleOptions.length === 0 ? (
                      <p className="text-muted-foreground text-xs">
                        {dashboardT('users.noAllowedRoles')}
                      </p>
                    ) : null}
                  </div>

                  <UserAgencyFields form={form} disabled={pending} />

                  {publisherRoleSelected ? (
                    <div className="space-y-1.5">
                      <Label className="text-[13px] font-semibold">
                        {dashboardT('users.publishers')} <span className="text-destructive">*</span>
                      </Label>
                      <Controller
                        control={form.control}
                        name="publisherIds"
                        render={({ field }) => (
                          <AutocompleteMultiSelect
                            label={dashboardT('users.publishers')}
                            options={publishers.options.map((publisher) => ({
                              id: publisher.id,
                              label: publisher.name,
                            }))}
                            value={field.value}
                            onValueChange={field.onChange}
                            loading={publishers.loading}
                            disabled={!networkId || pending}
                            placeholder={dashboardT('users.searchPublishers')}
                            emptyMessage={dashboardT('users.noPublishers')}
                          />
                        )}
                      />
                      {errors.publisherIds?.message ? (
                        <p className="text-destructive text-xs">
                          {translateValidationMessage(errors.publisherIds.message)}
                        </p>
                      ) : null}
                      {publishers.error ? (
                        <p className="text-destructive text-xs">
                          {dashboardT('users.publishersError')}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}

              {superAdmin ? (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold">
                      {dashboardT('users.networks')} <span className="text-destructive">*</span>
                    </Label>
                    <AutocompleteMultiSelect
                      label={dashboardT('users.networks')}
                      options={networkOptions.map((network) => ({
                        id: String(network.id),
                        label: network.name,
                      }))}
                      value={membershipNetworkIds}
                      onValueChange={(selectedNetworkIds) => {
                        const selected = new Set(selectedNetworkIds);
                        const nextMemberships = selectedNetworkIds.map(
                          (selectedNetworkId) =>
                            memberships.find(
                              (membership) => membership.networkId === selectedNetworkId,
                            ) ?? {
                              networkId: selectedNetworkId,
                              roles: [],
                              publisherIds: [],
                              agencyIds: [],
                            },
                        );
                        form.setValue(
                          'memberships',
                          nextMemberships.filter((membership) =>
                            selected.has(membership.networkId),
                          ),
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }}
                      loading={networks.isFetching}
                      disabled={networks.isError || pending}
                      placeholder={dashboardT('users.selectNetworks')}
                      emptyMessage={dashboardT('users.noNetworks')}
                      inlineSelectedValues
                    />
                    {errors.memberships?.message ? (
                      <p className="text-destructive text-xs">
                        {translateValidationMessage(errors.memberships.message)}
                      </p>
                    ) : null}
                    {networks.isError ? (
                      <p className="text-destructive text-xs">
                        {dashboardT('users.networksError')}
                      </p>
                    ) : null}
                  </div>

                  {memberships.map((membership, index) => {
                    const network = networkOptions.find(
                      (option) => String(option.id) === membership.networkId,
                    );
                    if (!network) return null;
                    return (
                      <UserNetworkMembershipFields
                        key={membership.networkId}
                        form={form}
                        index={index}
                        network={network}
                        allowedRoles={membershipAllowedRoles[index] ?? emptyAllowedRolesQuery}
                        errors={errors.memberships?.[index]}
                        pending={pending}
                        onPublisherOptionsUnavailableChange={setPublisherMembershipUnavailable}
                        onRemove={() => {
                          form.setValue(
                            'memberships',
                            memberships.filter((_, membershipIndex) => membershipIndex !== index),
                            { shouldDirty: true, shouldValidate: true },
                          );
                        }}
                      />
                    );
                  })}
                </div>
              ) : null}

              {errors.root?.server?.message ? (
                <p role="alert" className="text-destructive text-sm">
                  {errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
                {dashboardT('users.cancel')}
              </Button>
              <Button type="submit" disabled={saveDisabled}>
                {pending ? dashboardT('users.saving') : dashboardT('users.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
